// Doc 14 §1 — the checklist behind "Refresh" on an applied template. Three groups:
// what the template would add, what it would update, and what changed on both sides.
// Adds and updates start ticked; conflicts don't, and show the trip's value beside the
// template's so the user can see what ticking would overwrite.

import type { ReactNode } from "react";
import { Badge, Button, Checkbox, Dialog, ListRow, vars } from "@pack-it-up/ui-web";
import type { RefreshCounts, RefreshEntry, RefreshEntryStatus } from "@pack-it-up/core";
import { refreshEntryKey } from "@pack-it-up/core";

export interface RefreshTemplateDialogProps {
  templateName: string;
  entries: RefreshEntry[];
  checked: ReadonlySet<string>;
  counts: RefreshCounts;
  loading: boolean;
  error: string | null;
  onToggle: (key: string) => void;
  onConfirm: () => void;
  onClose: () => void;
}

const GROUPS: Array<{ status: RefreshEntryStatus; title: string; hint: string }> = [
  { status: "add", title: "New items", hint: "In the template, not yet on this trip." },
  {
    status: "update",
    title: "Updated in the template",
    hint: "You haven't changed these on the trip, so the template's version can replace them.",
  },
  {
    status: "conflict",
    title: "Changed on both sides",
    hint: "Ticking one replaces your trip's version with the template's.",
  },
];

// A short, human description of what the template would write. Deliberately terse: the
// full row editor is one click away once the refresh has landed.
const describe = (e: RefreshEntry): string | null => {
  if (e.status === "add") return null;
  const now = e.templateValue;
  const was = e.currentValue;
  if (was === undefined) return null;
  const parts: string[] = [];
  if (was.name !== now.name) parts.push(`renamed from “${was.name}”`);
  if (was.quantity !== now.quantity && now.quantity !== undefined) parts.push(`quantity ${was.quantity ?? 1} → ${now.quantity}`);
  if ((was.description ?? "") !== (now.description ?? "")) parts.push("description");
  if ((was.notes ?? "") !== (now.notes ?? "")) parts.push("notes");
  if ((was.tags ?? []).join(",") !== (now.tags ?? []).join(",")) parts.push("tags");
  if (JSON.stringify(was.assignment) !== JSON.stringify(now.assignment)) parts.push("who packs it");
  if (JSON.stringify(was.relativeDueOffset) !== JSON.stringify(now.relativeDueOffset)) parts.push("due date");
  return parts.length > 0 ? parts.join(" · ") : null;
};

const sectionStyle = {
  marginTop: vars.space.lg,
  border: `1px solid ${vars.color.border}`,
  borderRadius: vars.radius.md,
  background: vars.color.surface,
  overflow: "hidden",
} as const;

const headerStyle = {
  padding: `${vars.space.sm} ${vars.space.md}`,
  borderBottom: `1px solid ${vars.color.border}`,
} as const;

const Group = ({
  title,
  hint,
  rows,
  checked,
  onToggle,
}: {
  title: string;
  hint: string;
  rows: RefreshEntry[];
  checked: ReadonlySet<string>;
  onToggle: (key: string) => void;
}): ReactNode => {
  if (rows.length === 0) return null;
  return (
    <section aria-label={title} style={sectionStyle}>
      <div style={headerStyle}>
        <div style={{ fontSize: vars.font.sizeSm, fontWeight: vars.font.weightMedium, color: vars.color.text }}>
          {title}
          <span style={{ color: vars.color.textMuted, fontWeight: vars.font.weightRegular, marginLeft: "6px" }}>
            {rows.length}
          </span>
        </div>
        <div style={{ fontSize: vars.font.sizeXs, color: vars.color.textMuted, marginTop: "2px" }}>{hint}</div>
      </div>
      {rows.map((e) => {
        const key = refreshEntryKey(e);
        const detail = describe(e);
        return (
          <ListRow
            key={key}
            aria-label={e.name}
            leading={
              <Checkbox
                variant="sync"
                checked={checked.has(key)}
                onChange={() => onToggle(key)}
                aria-label={`${e.status === "add" ? "Add" : "Update"} ${e.name}`}
              />
            }
            trailing={
              <span style={{ display: "inline-flex", alignItems: "center", gap: vars.space.xs }}>
                {e.assignmentSkipped && (
                  <span style={{ fontSize: vars.font.size2xs, color: vars.color.textMuted, fontStyle: "italic" }}>
                    who packs it not copied
                  </span>
                )}
                <Badge tone="neutral">{e.categoryName}</Badge>
              </span>
            }
          >
            <span style={{ fontSize: "13.5px", color: vars.color.text }}>{e.name}</span>
            {detail !== null && (
              <span style={{ display: "block", fontSize: vars.font.sizeXs, color: vars.color.textMuted }}>{detail}</span>
            )}
          </ListRow>
        );
      })}
    </section>
  );
};

const summarise = (counts: RefreshCounts): string => {
  const parts: string[] = [];
  if (counts.adds > 0) parts.push(`Add ${counts.adds}`);
  if (counts.updates > 0) parts.push(`update ${counts.updates}`);
  return parts.length === 0 ? "Nothing selected" : parts.join(", ");
};

export const RefreshTemplateDialog = ({
  templateName,
  entries,
  checked,
  counts,
  loading,
  error,
  onToggle,
  onConfirm,
  onClose,
}: RefreshTemplateDialogProps): ReactNode => {
  const actionable = entries.filter((e) => e.status !== "unchanged");
  const nothingToDo = !loading && error === null && actionable.length === 0;

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Refresh from “${templateName}”`}
      subtitle="Adds what the template gained since you imported it. Nothing is removed, and checked-off items stay checked off."
      footer={
        <div style={{ display: "flex", alignItems: "center", width: "100%", justifyContent: "space-between" }}>
          <span style={{ fontSize: vars.font.sizeSm, color: vars.color.textMuted }}>
            {nothingToDo ? "" : summarise(counts)}
          </span>
          <div style={{ display: "flex", gap: vars.space.sm }}>
            <Button variant="ghost" size="sm" onClick={onClose}>
              {nothingToDo ? "Close" : "Cancel"}
            </Button>
            {!nothingToDo && (
              <Button size="sm" onClick={onConfirm} disabled={loading || counts.adds + counts.updates === 0}>
                Refresh
              </Button>
            )}
          </div>
        </div>
      }
    >
      {loading && (
        <div style={{ padding: vars.space.lg, color: vars.color.textMuted, fontSize: vars.font.sizeSm, fontStyle: "italic" }}>
          Loading the template…
        </div>
      )}
      {error !== null && (
        <div role="alert" style={{ padding: vars.space.md, color: vars.color.danger, fontSize: vars.font.sizeSm }}>
          {error}
        </div>
      )}
      {nothingToDo && (
        <div style={{ padding: vars.space.lg, color: vars.color.textMuted, fontSize: vars.font.sizeSm, textAlign: "center" }}>
          This trip is already up to date with <strong>{templateName}</strong>.
        </div>
      )}
      {GROUPS.map((g) => (
        <Group
          key={g.status}
          title={g.title}
          hint={g.hint}
          rows={actionable.filter((e) => e.status === g.status)}
          checked={checked}
          onToggle={onToggle}
        />
      ))}
    </Dialog>
  );
};
